import type { ApiError } from '@/api/http/types'
import { SP_OTP_LOCKOUT_FALLBACK_SECONDS } from './otp-messages'

export interface LockoutState {
  lockedUntilMs: number
  retryAfterSeconds: number
}

export interface LockoutFromApiErrorOptions {
  /** Default when envelope `data` and Retry-After are absent (login lockout: 300s). */
  fallbackRetrySeconds?: number
}

function parseRetryHeader(retryAfter: string | undefined): number | null {
  if (!retryAfter) return null
  const n = Number.parseInt(retryAfter, 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Derive lockout end instant from identity envelope `data` or Retry-After. */
export function lockoutFromApiError(
  error: ApiError,
  retryAfterHeader?: string | null,
  options?: LockoutFromApiErrorOptions
): LockoutState | null {
  const isLockout =
    error.status === 429 ||
    error.status === 403 ||
    error.isRateLimited ||
    error.isForbidden

  if (!isLockout) return null

  const data = error.data
  let retryFromData: number | null = null
  if (typeof data?.retryAfterSeconds === 'number') {
    retryFromData = data.retryAfterSeconds
  } else if (typeof data?.canRetryAfter === 'number') {
    retryFromData = data.canRetryAfter
  }

  const retryFromHeader = parseRetryHeader(retryAfterHeader ?? undefined)
  const fallbackRetrySeconds = options?.fallbackRetrySeconds ?? 300
  const retryAfterSeconds = retryFromData ?? retryFromHeader ?? fallbackRetrySeconds

  let lockedUntilMs: number
  if (typeof data?.lockedUntil === 'string') {
    const parsed = Date.parse(data.lockedUntil)
    lockedUntilMs = Number.isFinite(parsed)
      ? parsed
      : Date.now() + retryAfterSeconds * 1000
  } else {
    lockedUntilMs = Date.now() + retryAfterSeconds * 1000
  }

  return { lockedUntilMs, retryAfterSeconds }
}

/** OTP verify/resend flows — 2-minute fallback matches identity SP OTP timings. */
export function lockoutFromOtpApiError(
  error: ApiError,
  retryAfterHeader?: string | null
): LockoutState | null {
  return lockoutFromApiError(error, retryAfterHeader, {
    fallbackRetrySeconds: SP_OTP_LOCKOUT_FALLBACK_SECONDS,
  })
}

export function formatLockoutCountdown(remainingSeconds: number): string {
  const m = Math.floor(remainingSeconds / 60)
  const s = remainingSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
